import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Botao } from './base';
import { Erro, Vazio } from './estados';
import { ehFalhaDeChunk, recarregarPagina, sinalizarNovaVersao } from '../nucleo/versao';
import { registrarErro } from '../nucleo/log-erros';

/** Fase 2 (W39): um pedaço do app que não carregou (sumiu com o deploy) pede para atualizar, em vez da tela de erro. */
function TelaMudou() {
  const { t } = useTranslation();
  return (
    <div role="alert" data-tela-mudou>
      <Vazio
        icone={<RefreshCw className="size-5" aria-hidden />}
        titulo={t('novaVersao.texto')}
        texto={t('novaVersao.telaMudou')}
        acao={
          <Botao variante="primario" onClick={recarregarPagina}>
            {t('novaVersao.atualizar')}
          </Botao>
        }
      />
    </div>
  );
}

class Limite extends Component<{ children: ReactNode; aoTentar: () => void }, { houve: boolean; chunk: boolean }> {
  override state = { houve: false, chunk: false };

  static getDerivedStateFromError(erro: unknown) {
    return { houve: true, chunk: ehFalhaDeChunk(erro) };
  }

  override componentDidCatch(erro: unknown, info: ErrorInfo) {
    if (ehFalhaDeChunk(erro)) {
      sinalizarNovaVersao('chunk');
      return;
    }
    console.error('Falha de renderização', erro, info.componentStack);
    // CRM novo desenho, lote 0: a tela que quebrou entra no log de erros.
    void registrarErro(erro, { origem: 'render' });
  }

  override render() {
    if (!this.state.houve) return this.props.children;
    if (this.state.chunk) return <TelaMudou />;
    return (
      <Erro
        aoTentar={() => {
          this.props.aoTentar();
          this.setState({ houve: false, chunk: false });
        }}
      />
    );
  }
}

/**
 * Review r4 (robustez): um valor inesperado do servidor que faça a renderização lançar exceção não pode
 * deixar a tela inteira em branco. Mostra o componente de erro com "Tentar de novo": descarta os dados em cache
 * (o dado quebrado estaria lá) e renderiza de novo, o que refaz as consultas. A sessão não é descartada.
 * Use com `key` mudando junto com a rota para o erro não acompanhar o usuário a outra tela.
 * Fase 2 (W39): a falha de carga de um pedaço do app (deploy novo) mostra "Nova versão disponível" com "Atualizar".
 */
export function LimiteErro({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  return <Limite aoTentar={() => qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'sessao' })}>{children}</Limite>;
}
